import { useMutation } from '@apollo/client'
import { Box, Button } from '@material-ui/core'
import { useState } from 'react'
import { toast } from 'react-toastify'

import { ListFilter } from '../../components/ListFilter'
import SectionTitle from '../../components/SectionTitle'
import * as notificationGraphql from '../../graphql/notifications'
import { useCursorPage } from '../../helpers/useCursorPage'
import { NotificationListSkeleton } from './NotificationListSkeleton'
import NotificationResourceTable from './NotificationResourceTable'
import { DELIVERY_COLUMNS } from './resourceColumns'
import {
  ErrorState,
  LoadMoreButton,
  idempotencyKey,
  RETAINED_RESULTS,
  t,
  useFilteredRows
} from './shared'
import { NotificationDeliveryRow } from './types'

export function DeliveryList({
  admin,
  canManage,
  heading,
  queryVariables = {}
}: {
  admin: boolean
  canManage: boolean
  /**
   * The heading text. Required rather than derived: this list backs BOTH
   * the History tab and the admin Deliveries tab, so any value hardcoded
   * here would mislabel one of them.
   */
  heading: string
  queryVariables?: Record<string, unknown>
}) {
  const query = admin
    ? notificationGraphql.NOTIFICATION_ADMIN_DELIVERIES_QUERY
    : notificationGraphql.NOTIFICATION_DELIVERY_HISTORY_QUERY
  const resultKey = admin
    ? 'notificationAdminDeliveries'
    : 'notificationDeliveryHistory'
  const {
    items: rows,
    loading,
    error,
    hasMore,
    loadMore,
    refetch
  } = useCursorPage<Record<string, any>, NotificationDeliveryRow>({
    query,
    variables: queryVariables,
    maxRetained: RETAINED_RESULTS,
    getPage: (data) =>
      data && {
        items: data[resultKey]?.deliveries ?? [],
        // The admin query nests the token under `page`; the personal one does
        // not. That asymmetry was already handled here and is preserved.
        nextPageToken: admin
          ? data[resultKey]?.page?.nextPageToken
          : data[resultKey]?.nextPageToken
      },
    withItems: (data, items) => ({
      ...data,
      [resultKey]: { ...data[resultKey], deliveries: items }
    })
  })
  const [search, setSearch] = useState('')
  // Deliveries is the log an operator reaches for when hunting one message, so
  // it benefits from filtering more than the catalogue tabs do. Same mechanism
  // and the same honesty about scope: it narrows what has been LOADED.
  const visibleRows = useFilteredRows(rows, search)
  // An empty table after filtering must not read as "no deliveries exist" —
  // on this panel especially, since an operator searching for one message and
  // finding nothing is exactly the person who would conclude it was never
  // sent. Splits on hasMore, which comes from the SERVER's cursor and is
  // unaffected by client-side filtering: later pages may still hold matches.
  const emptyMessage = search.trim()
    ? hasMore
      ? t(
          'No loaded {title} match this filter yet. Load more to keep looking.',
          {
            title: t(heading).toLowerCase()
          }
        )
      : t('No loaded {title} match this filter.', {
          title: t(heading).toLowerCase()
        })
    : undefined
  const [retry, { loading: retrying }] = useMutation(
    notificationGraphql.RETRY_NOTIFICATION_DELIVERY_MUTATION
  )
  // `&& !data` matches the inbox guard above. The bare `loading` this replaced
  // predates the skeleton, and mattered less with a spinner: any refetch — the
  // Retry mutation on this very panel — blanked the rendered rows. A
  // full-height skeleton makes that far more disruptive, discarding scroll
  // position mid-interaction. Showing stale rows while the refetch lands is the
  // better trade; the panel is not stale for long.
  if (loading && !rows.length)
    return (
      <NotificationListSkeleton
        label={t('Loading deliveries')}
        variant="resource"
      />
    )
  if (error)
    return (
      <ErrorState
        message="Unable to load delivery history."
        onRetry={() => refetch()}
      />
    )
  return (
    <Box>
      <SectionTitle title={t(heading)} />
      <ListFilter
        label={heading}
        value={search}
        onChange={setSearch}
        shown={visibleRows.length}
        loaded={rows.length}
      />
      <NotificationResourceTable
        title="Deliveries"
        rows={visibleRows}
        emptyMessage={emptyMessage}
        columns={DELIVERY_COLUMNS}
        loading={loading}
        settled={!loading && !error}
        reloadData={() => refetch()}
        renderActions={(row) =>
          admin && canManage && row.status === 'FAILED' ? (
            <Button
              disabled={retrying}
              onClick={async () => {
                try {
                  await retry({
                    variables: { id: row.id, idempotencyKey: idempotencyKey() }
                  })
                  await refetch()
                } catch (actionError) {
                  toast.error(t('Unable to retry delivery.'))
                }
              }}>
              {retrying ? t('Retrying…') : t('Retry')}
            </Button>
          ) : null
        }
      />
      {hasMore && <LoadMoreButton onLoadMore={async () => loadMore()} />}
    </Box>
  )
}
